import {Component, HostBinding, NgZone, OnInit} from '@angular/core';
import {AlertController, Platform} from '@ionic/angular';
import {App as CapacitorApp, URLOpenListenerEvent} from '@capacitor/app';
import {Capacitor} from '@capacitor/core';
import {SplashScreen} from '@capacitor/splash-screen';
import {StatusBar, Style} from '@capacitor/status-bar';
import {Store} from '@ngrx/store';
import {IAppState} from './models/IAppState';
import {Router} from '@angular/router';
import {each} from 'lodash';
import {loadFromLocalStorage} from './store/actions/store.actions';
import {TranslationService} from './translation';
import {PushNotificationService} from './services/push-notification.service';
import {LoadingService} from './services/loading.service';
import moment from 'moment';
import {Observable} from 'rxjs';
import {IUserData, MemberType} from './models/IUserData';
import {CometchatService} from './services/cometchat.service';
import {environment} from '../environments/environment';
import {BaseComponent} from './pages/base.page';
import * as Sentry from '@sentry/browser';
import {ILocalUser} from './models/ILocalUser';
import {IAuthData} from './models/IAuthData';
import {AppVersionService} from './services/app-version.service';
import {DeviceService} from './services/device.service';
import {BrowserService} from './services/browser.service';


@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent extends BaseComponent implements OnInit {
  public menuLoaded = false;
  public selectedIndex = 0;
  public language$: Observable<string> = this.store.select((state) => state.AppStateReducer.language);
  public isAndroid = this.platform.is('android');

  private memberType$: Observable<MemberType> = this.store.select((state) => {
    if (state.AppStateReducer.userData) {
      return state.AppStateReducer.userData.memberType;
    } else {
      return null;
    }
  });

  public userData: IUserData;
  public localUser: ILocalUser;

  private userData$: Observable<IUserData> = this.store.select((state) => state.AppStateReducer.userData);
  private authData$: Observable<IAuthData> = this.store.select((state) => state.AppStateReducer.authData);
  private localUser$: Observable<ILocalUser> = this.store.select((state) => state.AppStateReducer.localUser);
  public instantLogin = true;

  private loggedInAsLocationUser$: Observable<boolean> = this.store.select((state) => {
    return state.AppStateReducer.loggedInAsLocationUser;
  });

  public memberType: MemberType;
  public role: string;
  public loggedInAsLocationUser = false;
  @HostBinding('class') public theme: string;

  public appPages = [
    {
      title: 'Reservations',
      url: '/reservations-list',
      icon: 'time',
      enabled: () => true,
      selected: () => window.location.pathname.indexOf('/reservation') > -1
    },
    {
      title: 'Check-ins',
      url: '/checkin-list/ticket',
      icon: 'pricetags',
      enabled: () => true,
      selected: () => window.location.pathname.indexOf('/checkin-list') > -1 || window.location.pathname.indexOf('/check-in') > -1
    },
    {
      title: 'Chats',
      url: '/checkins-chats',
      icon: 'chatbubbles',
      enabled: () => true,
      count: () => this.cometchatService.getUnreadMessagesCount(),
      selected: () => window.location.pathname.indexOf('chat') > -1
    },
    {
      title: 'Search location',
      url: '/search-location',
      icon: 'search',
      enabled: () => this.memberType === MemberType.visitor && !this.instantLogin,
      selected: () => window.location.pathname.indexOf('/search-location') > -1
    },
    {
      title: 'Settings',
      url: '/settings-menu',
      icon: 'settings',
      enabled: () => true,
      selected: () => window.location.pathname.indexOf('/settings') > -1
    },
    {
      title: 'Share',
      url: '/share',
      icon: 'share-social',
      enabled: () => this.platform.is('mobile'),
      selected: () => window.location.pathname.indexOf('/share') > -1
    },
    {
     title: 'Engineer\'s Menu',
     url: '/engineer',
     icon: 'construct',
     enabled: () => localStorage[`engineer`] === '1',
     selected: () => window.location.pathname.indexOf('/engineer') > -1
    }
  ];
  private readonly deeplinkHosts = [
    'app-dev.safe-checkin.com',
    'app-stage.safe-checkin.com',
    'app.safe-checkin.com',
    'sci.safe-checkin.com',
    'localhost'
  ];
  constructor(
    private platform: Platform,
    private ngZone: NgZone,
    private router: Router,
    private store: Store<{ AppStateReducer: IAppState }>,
    private translation: TranslationService,
    private pushNotificationService: PushNotificationService,
    private versionService: AppVersionService,
    public cometchatService: CometchatService,
    public alertController: AlertController,
    public loadingService: LoadingService,
    private browserService: BrowserService,
    private deviceService: DeviceService
  ) {
    super();
    translation.setLanguage('en');
    this.initTranslations();
    this.initializeApp();
  }

  initTranslations() {
    this.translation.setLanguage('en');
    each(this.appPages, (page) => {
      this.translation.get(page.title).then((res: string) => {
        page.title = res;
      });
    });
    this.language$.pipe().subscribe((language: string) => {
      console.log('language', language);
      this.translation.setLanguage(language);
      moment.locale(language);
    });
  }
  initTheme() {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    console.log('prefersDark', prefersDark);
    this.toggleDarkTheme(prefersDark.matches);

    prefersDark.addEventListener('change', (mediaQuery) => {
      this.toggleDarkTheme(mediaQuery.matches);
    });
  }
  toggleDarkTheme(shouldAdd) {
    document.body.classList.toggle('dark', shouldAdd);
  }
  initializeApp() {
    this.platform.ready().then(async () => {
      this.store.dispatch(loadFromLocalStorage());
      await this.configureNativeChrome();
      // Request native push token as early as possible so iOS verification has AppToken ready.
      await this.pushNotificationService.getDeviceRegistrationIdAndOperatingSystem();
      await this.pushNotificationService.clearPushDeliveredNotifications();
      this.initMemberType();
      this.initLocationUser();
      this.initDeeplinks();
      this.initTheme();
      await this.defineAndroidSafeArea();
      setTimeout(() => (this.menuLoaded = true), 500);
    });
  }

  ngOnInit() {
    const path = window.location.pathname;
    if (path) {
      this.selectedIndex = this.appPages.findIndex((page) => path.indexOf(page.url) > -1);
    }
    this.authData$.subscribe((authData: IAuthData) => {
      this.instantLogin = authData?.instantLogin || false;
    });
    this.userData$.subscribe((userData: IUserData) => {
      this.userData = userData;
      this.setSentryContext();
      if (userData) {
        this.pushNotificationService.initiateWebPush();
      }
    });
    this.localUser$.subscribe((localUser: ILocalUser) => {
      this.localUser = localUser;
      this.setSentryContext()
    });
  }

  private setSentryContext() {
    this.deviceService.getInfo().then((info) => {
      Sentry.setExtra('userDevice', info);
    });
    Sentry.setExtra('userData', this.userData);
    Sentry.setContext('userData', this.userData as any);
    Sentry.setExtra('localUser', this.localUser);
    Sentry.setContext('localUser', this.localUser as any);
  }

  initMemberType() {
    return this.memberType$.subscribe((memberType) => {
      this.memberType = memberType;
    });
  }

  initLocationUser() {
    return this.loggedInAsLocationUser$.subscribe((loggedInAsLocationUser) => {
      this.loggedInAsLocationUser = loggedInAsLocationUser;
    });
  }

  private async configureNativeChrome() {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    try {
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setStyle({style: Style.Default});
    } catch (error) {
      console.warn('Failed to set status bar style', error);
    }

    try {
      await SplashScreen.hide({fadeOutDuration: 300});
    } catch (error) {
      console.warn('Failed to hide splash screen', error);
    }
  }

  initDeeplinks() {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    CapacitorApp.getLaunchUrl().then((urlOpen) => {
      if (urlOpen?.url) {
        this.handleDeeplink(urlOpen.url);
      }
    });

    CapacitorApp.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.handleDeeplink(event.url);
    });
  }

  private handleDeeplink(url: string) {
    if (!url) {
      return;
    }

    // When the OIDC popup flow is active, the popup navigator handles the callback.
    if ((window as any).__oidcPopupInProgress) {
      return;
    }

    try {
      const parsed = new URL(url);
      if (this.deeplinkHosts.length && !this.deeplinkHosts.includes(parsed.hostname)) {
        return;
      }

      const queryParams: any = {};
      parsed.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });

      const path = parsed.pathname || '/';

      this.ngZone.run(() => {
        if (path === '/' || path === '') {
          this.router.navigateByUrl('/');
        } else {
          this.router.navigate([path], {queryParams, replaceUrl: true});
        }
      });
    } catch (error) {
      console.warn('Failed to handle deep link', error);
    }
  }

  openLocationAdmin() {
    return this.browserService.openExternal(environment.adminUrl);
  }

  displayShortcutAppAlert() {
    this.versionService.displayShortcutAppAlert();
  }

  private async defineAndroidSafeArea() { // Fixes an android edge-to-edge-support issue of 15 version. Will be fixed in cordova-android 14.x.x
    if (!this.platform.is('android')) {
      return;
    }
    const osVersion = await this.deviceService.getOsVersion();
    if (+osVersion >= 15) {
      const defaultSafeArea = Math.floor(window.screen.height * 0.05);
      const rootStyle = getComputedStyle(document.documentElement);
      const safeBottomArea: number = parseFloat(rootStyle.getPropertyValue('--ion-safe-area-bottom')) || defaultSafeArea;
      const safeTopArea: number = parseFloat(rootStyle.getPropertyValue('--ion-safe-area-top')) || defaultSafeArea;

      document.documentElement.style.setProperty('--ion-safe-area-bottom', `${safeBottomArea}px`);
      document.documentElement.style.setProperty('--ion-safe-area-top', `${safeTopArea}px`);
      document.body.classList.add('android-safe-area');
    }
  }
}
